import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook that rotates through an array of image sources every `intervalMs` milliseconds.
 * Returns the current image src and a fade state for smooth transitions.
 * Images are shuffled on mount so each visitor gets a different order.
 */
export function useRotatingImage(
  images: string[],
  intervalMs = 15000,
): { currentSrc: string; isFading: boolean } {
  const shuffled = useRef<string[]>([]);

  // Shuffle once on mount (Fisher-Yates)
  if (shuffled.current.length === 0 && images.length > 0) {
    const arr = [...images];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    shuffled.current = arr;
  }

  const [index, setIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);

  const advance = useCallback(() => {
    if (shuffled.current.length <= 1) return;
    // Start fade-out
    setIsFading(true);
    // After fade-out completes, swap image and fade-in
    setTimeout(() => {
      setIndex(prev => (prev + 1) % shuffled.current.length);
      setIsFading(false);
    }, 600); // match CSS transition duration
  }, []);

  useEffect(() => {
    if (shuffled.current.length <= 1) return;
    const timer = setInterval(advance, intervalMs);
    return () => clearInterval(timer);
  }, [advance, intervalMs]);

  return {
    currentSrc: shuffled.current[index] || images[0] || '',
    isFading,
  };
}
